// enci.tokenManager.js
import { debugLog } from './enci.log.js';

export class TokenManager {
  constructor() {
    this.token = null;
    this.expirationTime = null;
    this.refreshThreshold = 5 * 60 * 1000; // 5 minutes before expiration
    this.lastRedirectTime = 0;
  }

  async getToken() {
    if (
      this.token &&
      this.expirationTime &&
      Date.now() < this.expirationTime - this.refreshThreshold
    ) {
      return this.token;
    }
    return this.refreshToken();
  }

  async refreshToken() {
    try {
      // Prevent redirect spam
      if (Date.now() - this.lastRedirectTime < 10000) {
        // 10 seconds cooldown
        throw new Error('Redirect cooldown active');
      }

      const response = await fetch('/api/Token/data');

      if (response.status === 401) {
        this.lastRedirectTime = Date.now();
        debugLog('Authentication required. Redirecting to login...');
        sessionStorage.setItem('redirectUrl', window.location.pathname);
        window.location.href = '/MicrosoftIdentity/Account/SignIn';
        throw new Error('Authentication required');
      }

      if (!response.ok) throw new Error('Failed to fetch token');

      const token = await response.text();
      const payload = JSON.parse(atob(token.split('.')[1]));
      this.token = token;
      this.expirationTime = payload.exp * 1000;
      debugLog('Token refreshed successfully');
      return this.token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  // For testing purposes only
  simulateExpiration() {
    debugLog('Simulating token expiration');
    this.expirationTime = Date.now() - 1000;
  }

  // Simulate complete de-authentication
  simulateDeauthentication() {
    debugLog('Simulating complete de-authentication');
    this.token = null;
    this.expirationTime = null;
  }

  async expireToken() {
    try {
      const response = await fetch('/api/Token/expire', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to expire token');
      }

      const result = await response.text();
      debugLog('Token expiration result:', result);

      // Clear local token data
      this.token = null;
      this.expirationTime = null;

      debugLog('Token expired successfully');
      return result;
    } catch (error) {
      console.error('Error expiring token:', error);
      throw error;
    }
  }

  // Helper method to get payload from token
  getPayload() {
    if (!this.token) return null;
    try {
      return JSON.parse(atob(this.token.split('.')[1]));
    } catch (error) {
      console.error('Error parsing token payload:', error);
      return null;
    }
  }

  // Helper method to check if token is expired
  isTokenExpired() {
    const payload = this.getPayload();
    if (!payload) return true;
    return Date.now() >= payload.exp * 1000;
  }
}

export const tokenManager = new TokenManager();

// Make tokenManager globally accessible for dev console
if (typeof window !== 'undefined') {
  window.tokenManager = tokenManager;
}

/*

tokenManager.simulateExpiration();
tokenManager.simulateDeauthentication();
tokenManager.getToken().then(token => console.log('New token:', token)).catch(error => console.error('Error:', error));
tokenManager.simulateDeauthentication();
fetch('/api/Token/data').then(response => console.log(response)).catch(error => console.error(error));
tokenManager.expireToken()
  .then(result => console.log('Token expired:', result))
  .catch(error => console.error('Error:', error));

*/